import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { endpoints } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAuth } from "../../../utils/useAuth";
import { OAuthApplication } from "../../auth";

dayjs.extend(customParseFormat);

/**
 * Normaliza data de nascimento para o formato exigido pelo Serpro V3 (DDMMYYYY).
 * Aceita DDMMYYYY, DD/MM/YYYY, YYYY-MM-DD, Dayjs ou Date.
 */
export function toSerproNascimento(
  value: string | dayjs.Dayjs | Date | null | undefined,
): string | null {
  if (value == null || value === "") return null;

  if (dayjs.isDayjs(value) || value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d.format("DDMMYYYY") : null;
  }

  const raw = String(value).trim();
  if (/^\d{8}$/.test(raw)) {
    const asDdmmyyyy = dayjs(raw, "DDMMYYYY", true);
    if (asDdmmyyyy.isValid()) return raw;
  }

  const formats = ["DD/MM/YYYY", "YYYY-MM-DD", "DDMMYYYY", "DD-MM-YYYY"];
  for (const format of formats) {
    const d = dayjs(raw, format, true);
    if (d.isValid()) return d.format("DDMMYYYY");
  }

  const fallback = dayjs(raw);
  return fallback.isValid() ? fallback.format("DDMMYYYY") : null;
}

/**
 * Consulta CPF via BFF de signup (proxy Serpro).
 * Contrato alinhado à Consulta CPF V3: exige CPF + data de nascimento (DDMMYYYY).
 */
export async function SearchCPF(
  cpf: string,
  nascimento: string,
  authType?: string,
) {
  const url = `${endpoints.SIGNUP}/1.0.0/searchCPF`;
  const nascimentoNorm = toSerproNascimento(nascimento);

  if (!nascimentoNorm) {
    return {
      status: HTTPResponseStatus.BadRequest,
      data: "Data de nascimento obrigatória para consulta de CPF",
    };
  }

  const bearerToken =
    authType === "application"
      ? await OAuthApplication()
      : await useAuth.getUserToken();

  const headers = {
    Accept: "*/*",
    Authorization: bearerToken ?? "",
    "Content-Type": "application/json",
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ cpf, nascimento: nascimentoNorm }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === HTTPResponseStatus.UnprocessableEntity) {
      return {
        status: HTTPResponseStatus.UnprocessableEntity,
        data: {},
      };
    }
    if (response.status === HTTPResponseStatus.ClientError) {
      return {
        status: HTTPResponseStatus.ClientError,
        data: {},
      };
    }

    if (!response.ok) {
      throw new Error(`SearchCPF: ${response.status}`);
    }

    const data = await response.json();
    return { status: HTTPResponseStatus.Success, data };
  } catch (error) {
    console.log("error", error);
    return {
      status: HTTPResponseStatus.BadRequest,
      data: `${error}`,
    };
  }
}
